import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  createClassAccessRequest,
  getClassesBySchoolId,
  getSharedTeachersByClassId,
  getTeachersBySchoolId
} from "../services/APIClient";
import {
  getSupportRequestErrorMessage,
  getSupportRequestReference,
  hasPlaceholderTargetId,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import { getDistinctClassGradeLabel } from "../utils/classDisplay";
import "./TeacherMakeResultsVisibleModal.css";

const getClassId = (classItem) => classItem?.id || classItem?._id || classItem?.classid || classItem?.classId;

const normaliseText = (value) => String(value || "").trim().replace(/\s+/g, " ");

const getClassOwnerId = (classItem) =>
  classItem?.classOwnerId || classItem?.class_owner_id || classItem?.ownerId || classItem?.owner_id;

const getClassCreatorId = (classItem) =>
  classItem?.teacherId || classItem?.teacher_id || classItem?.creatorId || classItem?.creator_id;

const getTeacherId = (teacher) =>
  typeof teacher === "string"
    ? teacher
    : teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId || teacher?.teacher_id;

const SHARED_TEACHER_FIELD_NAMES = [
  "sharedTeacherIds",
  "shared_teacher_ids",
  "sharedTeachers",
  "shared_teachers",
  "sharedWith",
  "shared_with",
  "additionalTeacherIds",
  "additional_teacher_ids",
  "additionalTeachers",
  "additional_teachers"
];

const getSharedTeacherAccessFromClass = (classItem) => {
  const sharedTeacherIds = new Set();
  const hasSharedAccessData = SHARED_TEACHER_FIELD_NAMES.some((fieldName) => {
    const value = classItem?.[fieldName];

    if (!Array.isArray(value)) {
      return false;
    }

    value.forEach((item) => {
      const id = getTeacherId(item) || getTeacherId(item?.teacher);

      if (id) {
        sharedTeacherIds.add(id);
      }
    });

    return true;
  });

  return {
    hasSharedAccessData,
    sharedTeacherIds
  };
};

const normaliseTeacher = (teacher) => ({
  id: getTeacherId(teacher),
  name: teacher?.name || teacher?.fullName || teacher?.displayName || "Class teacher"
});

const normaliseClass = (classItem, teachersById, countryGrades) => {
  const ownerId = getClassOwnerId(classItem);
  const creatorId = getClassCreatorId(classItem);
  const teacherId = ownerId || creatorId;
  const owner =
    teachersById.get(teacherId)?.name ||
    classItem?.teacherName ||
    classItem?.ownerName ||
    classItem?.classOwnerName ||
    "Class teacher";
  const year = classItem?.academicYear || classItem?.academic_year || classItem?.academicyear || "Current year";
  const name = classItem?.name || classItem?.className || "Unnamed class";
  const grade = getDistinctClassGradeLabel(classItem, name, countryGrades);

  return {
    ...classItem,
    id: getClassId(classItem),
    name,
    ownerId,
    creatorId,
    owner,
    year,
    grade,
    searchText: [name, grade, owner, year].filter(Boolean).join(" ").toLowerCase()
  };
};

const isClassOwnedOrCreatedByTeacher = (classItem, teacherid) =>
  Boolean(
    teacherid &&
    (classItem?.ownerId === teacherid ||
      classItem?.creatorId === teacherid ||
      getClassOwnerId(classItem) === teacherid ||
      getClassCreatorId(classItem) === teacherid)
  );

const isClassSharedWithTeacherFromRow = (classItem, teacherid) => {
  const { hasSharedAccessData, sharedTeacherIds } = getSharedTeacherAccessFromClass(classItem);

  return {
    hasSharedAccessData,
    isSharedWithTeacher: Boolean(teacherid && sharedTeacherIds.has(teacherid))
  };
};

function TeacherMakeResultsVisibleModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  teacherid,
  prefill = null,
  onGoToClassList = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [classes, setClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [className, setClassName] = useState("");
  const [teacherName, setTeacherName] = useState("");
  const [assessmentName, setAssessmentName] = useState("");
  const [note, setNote] = useState("");
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [supportReference, setSupportReference] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);

  useEffect(() => {
    if (!show) return;
    setStage("form");
    const nextClassName = prefill?.className || "";
    const nextTeacherName = prefill?.teacherName || "";
    setClasses([]);
    setSearchTerm(prefill?.className || "");
    setSelectedClassId("");
    setClassName(nextClassName);
    setTeacherName(nextTeacherName);
    setAssessmentName(prefill?.assessmentName || "");
    setNote(prefill?.note || "");
    setError("");
    setIsSubmitting(false);
    setSupportReference("");
    setIsDuplicatePending(false);

    if (!schoolid) {
      setError("Classes could not be loaded. Please try again.");
      return;
    }

    setIsLoadingClasses(true);
    Promise.all([getTeachersBySchoolId(schoolid), getClassesBySchoolId(schoolid)])
      .then(([teachersResponse, classesResponse]) => {
        const teachersById = new Map(
          (Array.isArray(teachersResponse) ? teachersResponse : [])
            .map(normaliseTeacher)
            .filter((teacher) => teacher.id)
            .map((teacher) => [teacher.id, teacher])
        );
        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map((classItem) => normaliseClass(classItem, teachersById, countryGrades))
          .filter((classItem) => classItem.id && classItem.active !== false)
          .filter((classItem) => !isClassOwnedOrCreatedByTeacher(classItem, teacherid))
          .filter((classItem) => !isClassSharedWithTeacherFromRow(classItem, teacherid).isSharedWithTeacher)
          .sort((firstClass, secondClass) => firstClass.name.localeCompare(secondClass.name));
        const prefilledClass = normalisedClasses.find(
          (classItem) =>
            normaliseText(classItem.name).toLowerCase() === normaliseText(nextClassName).toLowerCase() &&
            (!nextTeacherName || normaliseText(classItem.owner).toLowerCase() === normaliseText(nextTeacherName).toLowerCase())
        );

        setClasses(normalisedClasses);
        if (prefilledClass) {
          setSelectedClassId(prefilledClass.id);
          setClassName(prefilledClass.name);
          setTeacherName(prefilledClass.owner);
        }
      })
      .catch((loadError) => {
        console.error("Error loading results-visible classes:", loadError);
        setError("Classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, schoolid, countryGrades, teacherid, prefill]);

  const canContinue = Boolean(selectedClassId && className.trim());
  const selectedClass = classes.find((classRow) => classRow.id === selectedClassId) || null;
  const filteredClasses = classes.filter((classRow) =>
    !searchTerm.trim() || classRow.searchText.includes(normaliseText(searchTerm).toLowerCase())
  );

  const selectClass = (classRow) => {
    setSelectedClassId(classRow.id);
    setClassName(classRow.name);
    setTeacherName(classRow.owner);
    setSearchTerm(classRow.name);
  };

  const resetForAnotherClass = () => {
    setStage("form");
    setSearchTerm("");
    setSelectedClassId("");
    setClassName("");
    setTeacherName("");
    setAssessmentName("");
    setNote("");
    setError("");
    setIsSubmitting(false);
    setSupportReference("");
    setIsDuplicatePending(false);
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference,
        message: "Results visibility request sent."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const isSelectedClassAlreadyAccessible = async () => {
    if (!selectedClass || !teacherid) {
      return false;
    }

    if (isClassOwnedOrCreatedByTeacher(selectedClass, teacherid)) {
      return true;
    }

    const rowSharedAccess = isClassSharedWithTeacherFromRow(selectedClass, teacherid);

    if (rowSharedAccess.isSharedWithTeacher) {
      return true;
    }

    if (rowSharedAccess.hasSharedAccessData) {
      return false;
    }

    const sharedTeachers = await getSharedTeachersByClassId(selectedClass.id);

    return (Array.isArray(sharedTeachers) ? sharedTeachers : []).some((teacher) => getTeacherId(teacher) === teacherid);
  };

  const sendResultsVisibleRequest = async () => {
    if (!canContinue || isSubmitting) {
      return;
    }

    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    if (!teacherid) {
      setError("Teacher context is missing. Refresh and try again.");
      return;
    }

    const target = {
      type: "class",
      classId: selectedClassId,
      targetTeacherId: teacherid
    };

    if (hasPlaceholderTargetId(target)) {
      setError("Choose a real class from search before sending this request.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const alreadyAccessible = await isSelectedClassAlreadyAccessible();

      if (alreadyAccessible) {
        setError("You already have access to this class. Open your class list to view it.");
        return;
      }

      const requestNote = [assessmentName, note.trim()].filter(Boolean).join(" | ") || undefined;
      const response = await createClassAccessRequest(schoolid, selectedClassId, {
        targetTeacherId: teacherid,
        note: requestNote
      });
      setSupportReference(getSupportRequestReference(response));
      setIsDuplicatePending(isDuplicatePendingResponse(response));
      setStage("success");
    } catch (submitError) {
      setError(getSupportRequestErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderClassResults = () => {
    if (isLoadingClasses) {
      return (
        <div className="results-visible-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (selectedClass) {
      return (
        <button
          key={selectedClass.id}
          type="button"
          className="results-visible-result selected"
          onClick={() => setSelectedClassId("")}
          aria-label={`Selected ${selectedClass.name}`}
          aria-pressed="true"
        >
          <span className="results-visible-radio" aria-hidden="true" />
          <span>
            <strong>{selectedClass.name}</strong>
            <span>{[selectedClass.grade, selectedClass.owner, selectedClass.year].filter(Boolean).join(" - ")}</span>
          </span>
        </button>
      );
    }

    if (!searchTerm.trim()) {
      return <p className="results-visible-muted">Start typing a class or teacher name.</p>;
    }

    if (filteredClasses.length < 1) {
      return <p className="results-visible-muted">No matching classes found.</p>;
    }

    return filteredClasses.slice(0, 5).map((classRow) => (
      <button
        key={classRow.id}
        type="button"
        className="results-visible-result"
        onClick={() => selectClass(classRow)}
        aria-label={`Select ${classRow.name}`}
        aria-pressed="false"
      >
        <span className="results-visible-radio" aria-hidden="true" />
        <span>
          <strong>{classRow.name}</strong>
          <span>{[classRow.grade, classRow.owner, classRow.year].filter(Boolean).join(" - ")}</span>
        </span>
      </button>
    ));
  };

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-make-results-visible-modal">
      {stage === "form" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Make Results Visible</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="results-visible-intro">This usually means the class has not been shared with you yet.</p>
            {error && <Alert variant="danger">{error}</Alert>}

            <Form.Group controlId="resultsVisibleClassSearch">
              <Form.Label>Which class or results can you not see?</Form.Label>
              <Form.Text as="div" className="results-visible-field-label">
                Search by class or teacher
              </Form.Text>
              <Form.Control
                type="search"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setSelectedClassId("");
                }}
                aria-label="Search class or teacher"
              />
              <Form.Text className="text-muted">
                Search the class or the teacher who owns it.
              </Form.Text>
            </Form.Group>

            <Form.Group className="visually-hidden" controlId="resultsVisibleTeacher">
              <Form.Label>Class owner or colleague</Form.Label>
              <Form.Control value={teacherName} readOnly />
            </Form.Group>

            <Form.Group className="visually-hidden" controlId="resultsVisibleAssessment">
              <Form.Label>Assessment or results</Form.Label>
              <Form.Control value={assessmentName} onChange={(event) => setAssessmentName(event.target.value)} />
            </Form.Group>

            <div className="results-visible-results" aria-label="Matching classes">
              {renderClassResults()}
            </div>

            <Form.Group className="results-visible-section" controlId="resultsVisibleNote">
              <Form.Label>Why do you need access?</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <span className="results-visible-footer-note">{canContinue ? `${className} selected.` : "Choose the class or results."}</span>
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={() => setStage("request")} disabled={!canContinue}>Request access</Button>
          </Modal.Footer>
        </>
      )}
      {stage === "request" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Request Class Access</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="results-visible-intro">This class has not been shared with you.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning" className="results-visible-warning">
              <strong>Send access request</strong>
              <span>Send a request so {className} and its results can be shared with you.</span>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <span className="results-visible-footer-note">Access request ready.</span>
            <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
            <Button variant="primary" onClick={sendResultsVisibleRequest} disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send request"}
            </Button>
          </Modal.Footer>
        </>
      )}
      {stage === "success" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Request Sent</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="results-visible-intro">You will be able to see the class and results when it is shared with you.</p>
            <Alert variant="success" className="results-visible-success">
              <span className="results-visible-ok-badge" aria-hidden="true">OK</span>
              <span>
                <strong>
                  {isDuplicatePending
                    ? `Your request to access ${className} is already in progress.`
                    : `Your request to access ${className} has been sent.`}
                </strong>
                <span>
                  You will be able to see the class and results when it is shared with you.
                  {supportReference ? `Reference: ${supportReference}. ` : ""}
                  The class and results will only become visible after the request is actioned.
                </span>
              </span>
            </Alert>
            <div className="results-visible-next">
              <strong>What next?</strong>
              <span>{embedded ? "Done returns to SENSAI" : "Done returns to Help Centre home"}. Request another class submits a separate review request.</span>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="results-visible-footer-note">Request submitted.</span>
            <Button variant="outline-secondary" onClick={resetForAnotherClass}>Request another class</Button>
            {!embedded && onGoToClassList && (
              <Button variant="outline-secondary" onClick={onGoToClassList}>Go to class list</Button>
            )}
            <Button variant="primary" onClick={handleDone}>Done</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherMakeResultsVisibleModal;
